/**
 * FanDetailModal — drill-in for a single fan handle.
 *
 * Shows: summary tile + per-creator breakdown table + last-20 calls timeline.
 * Sourced from /api/v1/reports/fan-detail/:fanHandle (with mock fallback).
 */
import React, { useRef, useEffect } from "react";
import { tokensV2 as T } from "./tokens.v2";
import { useFanDetail } from "./useTopSpenders.v2";
import { showProvenance } from "./env.v2";
import { useFocusTrap } from "../../utils/useFocusTrap";

function formatINR(n) {
  if (n == null) return "—";
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  if (num >= 1e7) return "Rs " + (num / 1e7).toFixed(2) + "Cr";
  if (num >= 1e5) return "Rs " + (num / 1e5).toFixed(2) + "L";
  if (num >= 1e3) return "Rs " + (num / 1e3).toFixed(1) + "K";
  return "Rs " + Math.round(num);
}
function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}
function formatTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}
function formatDuration(secs) {
  if (secs == null) return "—";
  const n = Number(secs);
  if (!Number.isFinite(n)) return String(secs);
  if (n < 60) return `${n}s`;
  const m = Math.floor(n / 60);
  const s = n % 60;
  return s ? `${m}m ${s}s` : `${m}m`;
}

function CreatorRow({ c, totalSpent }) {
  const pct = totalSpent > 0 ? (c.spent / totalSpent) * 100 : 0;
  const barFg = pct > 50 ? T.status.bad : pct > 25 ? T.status.warn : T.status.good;
  return (
    <tr style={{ borderBottom: `1px solid ${T.border.subtle}` }}>
      <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 600, color: T.text.primary }}>{c.name}</td>
      <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 700, color: T.text.primary, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{formatINR(c.spent)}</td>
      <td style={{ padding: "10px 12px", fontSize: 12, color: T.text.secondary, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{c.callCount}</td>
      <td style={{ padding: "10px 12px", minWidth: 140 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ flex: 1, height: 6, background: T.surface.canvas, borderRadius: 999, overflow: "hidden", minWidth: 60 }}>
            <div style={{ width: `${Math.min(100, pct)}%`, height: "100%", background: barFg, borderRadius: 999 }} />
          </div>
          <span style={{ fontSize: 11, fontWeight: 700, color: barFg, fontVariantNumeric: "tabular-nums", minWidth: 40, textAlign: "right" }}>{pct.toFixed(1)}%</span>
        </div>
      </td>
      <td style={{ padding: "10px 12px", fontSize: 11, color: T.text.muted }}>{formatDate(c.lastCallAt)}</td>
    </tr>
  );
}

export function FanDetailModal({ fanHandle, onClose, fromDate, toDate }) {
  const { data, loading, error } = useFanDetail(fanHandle, { from: fromDate, to: toDate });

  // Modal a11y: focus-trap + Escape-to-close + body scroll-lock while open.
  const modalRef = useRef(null);
  useFocusTrap(modalRef, { active: true, onEscape: onClose });
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  return (
    <div onClick={onClose} role="presentation" style={{
      position: "fixed", inset: 0, zIndex: 100,
      backgroundColor: "rgba(31,42,63,0.50)",
      display: "flex", alignItems: "stretch", justifyContent: "center",
      padding: 24,
    }}>
      <div
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Fan deep-dive ${fanHandle}`}
        style={{
        flex: 1, maxWidth: 980,
        backgroundColor: T.surface.canvas,
        borderRadius: 16,
        boxShadow: "0 20px 64px rgba(31,42,63,0.30)",
        display: "flex", flexDirection: "column",
        fontFamily: T.font.sans,
        overflow: "hidden",
      }}>
        <header style={{
          display: "flex", alignItems: "center", gap: 12,
          padding: "16px 22px",
          borderBottom: "1px solid " + T.border.subtle,
          backgroundColor: T.surface.card,
        }}>
          <span style={{
            width: 36, height: 36, borderRadius: 10,
            backgroundColor: "#FC801922", color: "#FC8019",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            fontWeight: 800, fontSize: 14, fontFamily: T.font.mono,
          }}>F</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: T.text.muted, textTransform: "uppercase", letterSpacing: "0.08em" }}>Fan deep-dive</div>
            <div style={{ fontSize: 17, fontWeight: 700, color: T.text.primary, letterSpacing: "-0.01em", fontFamily: T.font.mono }}>{fanHandle}</div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" style={{
            width: 36, height: 36, borderRadius: 10,
            backgroundColor: T.surface.canvas, border: "1px solid " + T.border.subtle,
            color: T.text.secondary, fontSize: 16,
            cursor: "pointer", outline: "none",
          }}>✕</button>
        </header>

        <div style={{ flex: 1, padding: 22, overflowY: "auto" }}>
          {loading && !data && <div style={{ color: T.text.muted, fontSize: 13 }}>Loading…</div>}

          {error && (
            <div style={{
              fontSize: 12, color: T.text.secondary,
              background: T.status.warnBg, border: `1px solid ${T.status.warn}`,
              padding: "8px 12px", borderRadius: 8, marginBottom: 12,
            }}>
              <strong>Could not fetch live data:</strong> {error}.
            </div>
          )}

          {data && (
            <>
              {/* Summary stat tiles */}
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(min(160px, 100%), 1fr))",
                gap: 10, marginBottom: 18,
              }}>
                <SummaryStat label="Lifetime spent" value={formatINR(data.summary.totalSpent)} />
                <SummaryStat label="Creators paid"  value={String(data.summary.creatorsPaid)} />
                <SummaryStat label="Calls"          value={String(data.summary.callCount)} />
                <SummaryStat label="First call"     value={formatDate(data.summary.firstCallAt)} />
                <SummaryStat label="Last call"      value={formatDate(data.summary.lastCallAt)} />
              </div>

              {/* Per-creator breakdown */}
              <div style={{ marginBottom: 18 }}>
                <SectionTitle>Per-creator breakdown</SectionTitle>
                <div style={{
                  background: T.surface.card, border: `1px solid ${T.border.subtle}`,
                  borderRadius: 12, overflow: "hidden",
                }}>
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: T.font.sans }}>
                      <thead>
                        <tr style={{ borderBottom: `2px solid ${T.border.subtle}`, background: T.surface.canvas }}>
                          <Th align="left">Creator</Th>
                          <Th align="right">Spent</Th>
                          <Th align="right">Calls</Th>
                          <Th align="left">% of fan's total</Th>
                          <Th align="left">Last call</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {(data.creators || []).map(c => (
                          <CreatorRow key={c.name} c={c} totalSpent={data.summary.totalSpent} />
                        ))}
                        {(!data.creators || data.creators.length === 0) && (
                          <tr><td colSpan={5} style={{ padding: 24, textAlign: "center", color: T.text.muted, fontSize: 13 }}>No creators in this window.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Recent calls timeline */}
              <div style={{ marginBottom: 18 }}>
                <SectionTitle hint="up to 20 most recent">Recent calls</SectionTitle>
                <div style={{
                  background: T.surface.card, border: `1px solid ${T.border.subtle}`,
                  borderRadius: 12,
                  display: "flex", flexDirection: "column",
                }}>
                  {(data.recentCalls || []).map((c, i) => (
                    <div key={i} style={{
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      padding: "10px 14px",
                      borderBottom: i < (data.recentCalls.length - 1) ? `1px solid ${T.border.subtle}` : "none",
                    }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: T.text.primary }}>{c.creatorName}</div>
                        <div style={{ fontSize: 11, color: T.text.muted }}>{formatTime(c.at)} · {formatDuration(c.duration)}</div>
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: T.text.primary, fontVariantNumeric: "tabular-nums" }}>
                        {formatINR(c.amount)}
                      </div>
                    </div>
                  ))}
                  {(!data.recentCalls || data.recentCalls.length === 0) && (
                    <div style={{ padding: 20, textAlign: "center", color: T.text.muted, fontSize: 13 }}>No calls in this window.</div>
                  )}
                </div>
              </div>

              {/* Provenance */}
              {showProvenance() && data.source && data.source.tables && (
                <div style={{
                  padding: 12,
                  background: T.surface.card,
                  border: `1px dashed ${T.border.default}`,
                  borderRadius: 10,
                  fontSize: 11, color: T.text.muted, fontFamily: T.font.mono,
                }}>
                  <div style={{ fontWeight: 700, marginBottom: 4, color: T.text.secondary }}>Where this data comes from (dev only)</div>
                  <div>Tables: {data.source.tables.join(" · ")}</div>
                  {data.source.query && <div>Query: {data.source.query}</div>}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryStat({ label, value }) {
  return (
    <div style={{
      background: T.surface.card,
      border: `1px solid ${T.border.subtle}`,
      borderRadius: 10, padding: "10px 14px",
      display: "flex", flexDirection: "column", gap: 4,
    }}>
      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.text.muted }}>{label}</span>
      <span style={{ fontSize: 18, fontWeight: 800, color: T.text.primary, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums" }}>{value}</span>
    </div>
  );
}
function SectionTitle({ children, hint }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8, padding: "0 4px" }}>
      <span style={{ fontSize: 13, fontWeight: 700, color: T.text.primary, letterSpacing: "-0.01em" }}>{children}</span>
      {hint && <span style={{ fontSize: 11, fontWeight: 600, color: T.text.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>{hint}</span>}
    </div>
  );
}
function Th({ children, align = "left" }) {
  return (
    <th style={{
      padding: "10px 12px", fontSize: 10, fontWeight: 700,
      letterSpacing: "0.08em", textTransform: "uppercase",
      color: T.text.muted, textAlign: align,
    }}>{children}</th>
  );
}
